import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarOutlined } from '@ant-design/icons';
import { Button, DatePicker, Dropdown } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import useQueryParamsContext from '@/contexts/query-params/useQueryParamsContext';
import { DATE_FORMATS, formatDateForBackend } from '@/utils/formUtils';

/**
 * A date-range funnel for a column header.
 *
 * Writes the same `[from, to]` query param the filter panel writes for the
 * same column, so the two always agree: set it here and the panel shows it,
 * clear it there and this funnel goes dark.
 *
 * Self-contained on purpose — it reads and writes the query params directly
 * rather than going through the text-search state the other header funnels
 * share, because a range is not a search term and does not fit that shape.
 *
 * @param {Object} props
 * @param {string} props.dataIndex - Query param the range is stored under.
 * @param {string} props.title - Column title, shown above the picker.
 */
export default function TableHeaderDateRange({
  dataIndex,
  title,
  placement = 'bottomRight',
}) {
  const { t } = useTranslation(['tables', 'forms']);
  const { queryParams, setQueryParams } = useQueryParamsContext();
  const [open, setOpen] = useState(false);

  const applied = useMemo(() => {
    const value = queryParams?.[dataIndex];

    // 🚨 `Array.isArray`, not a length check: a link carrying one value hands
    // this a string, and the picker must not crash on it.
    if (!Array.isArray(value) || value.length !== 2) return null;

    return [dayjs(value[0]), dayjs(value[1])];
  }, [dataIndex, queryParams]);

  const [draft, setDraft] = useState(applied);

  useEffect(() => {
    if (open) setDraft(applied);
  }, [open, applied]);

  const handleSubmit = () => {
    if (draft?.[0] && draft?.[1]) {
      setQueryParams({
        [dataIndex]: [
          formatDateForBackend(draft[0].startOf('day')),
          formatDateForBackend(draft[1].endOf('day')),
        ],
        offset: 1,
      });
    } else {
      setQueryParams({ [dataIndex]: undefined, offset: 1 });
    }

    setOpen(false);
  };

  const handleReset = () => {
    setDraft(null);
    setQueryParams({ [dataIndex]: undefined, offset: 1 });
    setOpen(false);
  };

  return (
    <div className="h-max">
      <Dropdown
        placement={placement}
        open={open}
        trigger={['click']}
        onOpenChange={(value, info) => {
          // Clicks inside the calendar are not "outside" the dropdown, but antd
          // reports them as a close; only the trigger and a real outside click
          // may close it.
          if (!value && info?.source === 'menu') return;
          setOpen(value);
        }}
        popupRender={() => (
          <div
            className="grid min-w-[260px] gap-3 rounded-md border-[3px] border-accent bg-white p-3 shadow-md"
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleSubmit();
            }}
          >
            <span className="font-semibold">{title}:</span>

            <DatePicker.RangePicker
              className="ant-range-picker w-full"
              format={DATE_FORMATS.DISPLAY}
              value={draft}
              onChange={(dates) => setDraft(dates)}
              placeholder={[
                t('startDate', { ns: 'forms' }),
                t('endDate', { ns: 'forms' }),
              ]}
              // Rendered inside the dropdown, so a click on a day is not a click
              // outside it and does not close the funnel half-way.
              getPopupContainer={(trigger) => trigger.parentElement}
            />

            <div className="flex gap-3">
              <Button
                size="small"
                type="default"
                shape="round"
                className="btn-default btn-default--hover h-8 w-1/2"
                onClick={handleReset}
              >
                {t('reset', { ns: 'forms' })}
              </Button>

              <Button
                size="small"
                type="primary"
                shape="round"
                className="btn-primary h-8 w-1/2"
                onClick={handleSubmit}
              >
                {t('submit', { ns: 'forms' })}
              </Button>
            </div>
          </div>
        )}
      >
        <Button
          type="default"
          shape="circle"
          className={clsx('btn-default h-6 w-6 bg-transparent p-0 shadow-none')}
          icon={
            <CalendarOutlined
              className={clsx(
                'rounded-full p-1.5',
                applied ? 'bg-white !text-accent shadow-md' : 'text-zinc-400'
              )}
            />
          }
        >
          <span className="sr-only">{t('search', { ns: 'forms' })}</span>
        </Button>
      </Dropdown>
    </div>
  );
}
